import { useEffect } from 'react';

const SITE_URL = 'https://www.eos-energy-systems.de';
const DEFAULT_IMAGE = `${SITE_URL}/seo-assets/photovoltaik-installation-konstanz.jpg`;

function setMetaAttribute(selector, createAttributes, valueAttribute, value) {
  let element = document.head.querySelector(selector);
  if (!element) {
    element = document.createElement('meta');
    Object.entries(createAttributes).forEach(([key, attrValue]) => element.setAttribute(key, attrValue));
    document.head.appendChild(element);
  }
  element.setAttribute(valueAttribute, value);
}

function setLink(rel, href) {
  let element = document.head.querySelector(`link[rel="${rel}"]`);
  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', rel);
    document.head.appendChild(element);
  }
  element.setAttribute('href', href);
}

function setJsonLd(id, data) {
  let element = document.getElementById(id);
  if (!element) {
    element = document.createElement('script');
    element.id = id;
    element.type = 'application/ld+json';
    document.head.appendChild(element);
  }
  element.textContent = JSON.stringify(data);
}

function removeJsonLd(id) {
  document.getElementById(id)?.remove();
}

export const seoPages = {
  home: {
    title: 'EOS Energy Systems | Photovoltaik & Elektrotechnik Stockach',
    description:
      'EOS Energy Systems plant und installiert Photovoltaik, Speicher, Wallboxen, Elektrotechnik und DGUV-Prüfungen in Stockach, Konstanz und der Bodensee-Region.',
    canonical: `${SITE_URL}/`,
    robots: 'index,follow,max-image-preview:large',
  },
  photovoltaik: {
    title: 'Photovoltaik Stockach | PV-Anlage & Speicher von EOS',
    description:
      'Photovoltaik-Anlagen, Stromspeicher, Wallbox und Energiemanagement von EOS Energy Systems für Stockach, Konstanz und die Bodensee-Region.',
    canonical: `${SITE_URL}/photovoltaik`,
    robots: 'index,follow,max-image-preview:large',
  },
  impressum: {
    title: 'Impressum | EOS Energy Systems GmbH',
    description: 'Impressum und rechtliche Angaben der EOS Energy Systems GmbH in Stockach.',
    canonical: `${SITE_URL}/impressum`,
    robots: 'index,follow',
  },
  internal: {
    title: 'Interner Bereich | EOS Energy Systems',
    description: 'Interner Bereich der EOS Energy Systems GmbH.',
    canonical: `${SITE_URL}/z-intern-394`,
    robots: 'noindex,nofollow,noarchive',
  },
};

export function Seo({ page = 'home' }) {
  useEffect(() => {
    const data = seoPages[page] || seoPages.home;
    document.documentElement.lang = 'de';
    document.title = data.title;

    setMetaAttribute('meta[name="description"]', { name: 'description' }, 'content', data.description);
    setMetaAttribute('meta[name="robots"]', { name: 'robots' }, 'content', data.robots);
    setMetaAttribute('meta[name="theme-color"]', { name: 'theme-color' }, 'content', '#00094d');

    setLink('canonical', data.canonical);

    setMetaAttribute('meta[property="og:locale"]', { property: 'og:locale' }, 'content', 'de_DE');
    setMetaAttribute('meta[property="og:type"]', { property: 'og:type' }, 'content', page === 'home' ? 'website' : 'article');
    setMetaAttribute('meta[property="og:site_name"]', { property: 'og:site_name' }, 'content', 'EOS Energy Systems GmbH');
    setMetaAttribute('meta[property="og:title"]', { property: 'og:title' }, 'content', data.title);
    setMetaAttribute('meta[property="og:description"]', { property: 'og:description' }, 'content', data.description);
    setMetaAttribute('meta[property="og:url"]', { property: 'og:url' }, 'content', data.canonical);
    setMetaAttribute('meta[property="og:image"]', { property: 'og:image' }, 'content', DEFAULT_IMAGE);
    setMetaAttribute('meta[property="og:image:alt"]', { property: 'og:image:alt' }, 'content', 'Photovoltaik und Elektrotechnik von EOS Energy Systems in der Bodensee-Region');

    setMetaAttribute('meta[name="twitter:card"]', { name: 'twitter:card' }, 'content', 'summary_large_image');
    setMetaAttribute('meta[name="twitter:title"]', { name: 'twitter:title' }, 'content', data.title);
    setMetaAttribute('meta[name="twitter:description"]', { name: 'twitter:description' }, 'content', data.description);
    setMetaAttribute('meta[name="twitter:image"]', { name: 'twitter:image' }, 'content', DEFAULT_IMAGE);

    if (page === 'home' || page === 'photovoltaik') {
      setJsonLd('eos-localbusiness-schema', {
        '@context': 'https://schema.org',
        '@type': ['LocalBusiness', 'Electrician'],
        '@id': `${SITE_URL}/#unternehmen`,
        name: 'EOS Energy Systems GmbH',
        url: SITE_URL,
        logo: `${SITE_URL}/EOS-Energy-Systems-Logo.png`,
        image: DEFAULT_IMAGE,
        description: data.description,
        address: {
          '@type': 'PostalAddress',
          addressLocality: 'Stockach',
          addressRegion: 'Baden-Württemberg',
          addressCountry: 'DE',
        },
        areaServed: ['Stockach', 'Konstanz', 'Radolfzell', 'Singen', 'Bodensee-Region'],
        knowsAbout: [
          'Photovoltaik',
          'Stromspeicher',
          'Elektroinstallation',
          'Wallbox Installation',
          'DGUV Prüfung',
          'Energiemanagement',
        ],
        makesOffer: [
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Photovoltaik-Anlagen und Speicher' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Elektroinstallation und Elektrotechnik' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Wallbox Installation' } },
          { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'DGUV Prüfung und Wartung' } },
        ],
      });
      setJsonLd('eos-website-schema', {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        '@id': `${SITE_URL}/#website`,
        name: 'EOS Energy Systems GmbH',
        url: SITE_URL,
        inLanguage: 'de-DE',
      });
      if (page === 'photovoltaik') {
        setJsonLd('eos-pv-service-schema', {
          '@context': 'https://schema.org',
          '@type': 'Service',
          '@id': `${SITE_URL}/photovoltaik#service`,
          name: 'Photovoltaik-Anlagen und Stromspeicher',
          serviceType: 'Photovoltaik Planung und Installation',
          provider: { '@id': `${SITE_URL}/#unternehmen` },
          areaServed: ['Stockach', 'Konstanz', 'Radolfzell', 'Singen', 'Bodensee-Region'],
          description: data.description,
        });
      } else {
        removeJsonLd('eos-pv-service-schema');
      }
    } else {
      removeJsonLd('eos-pv-service-schema');
    }
  }, [page]);

  return null;
}

export default Seo;
